import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  TemplateRef,
  input,
  output,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
} from '@angular/core';
import { MediaCardComponent } from '../media-card/media-card.component';
import { Media } from '../../models/media';
import { Observable, of, combineLatest, BehaviorSubject, switchMap, map, catchError } from 'rxjs';
import { HasId, HasType, Logger } from 'shared';

export interface MediaGridItem<T> {
  entity: T;
  media: Media;
  index: number;
}

export type MediaEntity = HasId & HasType;

@Component({
  selector: 'media-grid',
  imports: [CommonModule, MediaCardComponent],
  templateUrl: './media-grid.component.html',
  styleUrls: ['./media-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaGridComponent<T extends HasId>
  implements AfterViewInit, OnDestroy, OnChanges
{
  entities = input<T[]>([]);
  mediaOf = input.required<(entity: T) => Observable<Media | null>>();
  select = output<T>();

  @ContentChild('mediaBadges') mediaBadges?: TemplateRef<any>;
  @ContentChild('mediaDetails') mediaDetails?: TemplateRef<any>;

  protected logger = new Logger('MediaGridComponent');
  private resizeObserver?: ResizeObserver;
  _columns: number | undefined;

  private entities$ = new BehaviorSubject<T[]>([]);
  items$: Observable<MediaGridItem<T>[]>;

  trackById = (_: number, item: MediaGridItem<T>) => item.entity.id;

  mediaCoverRatio = 9/16 * 100;

  constructor(private elementRef: ElementRef<HTMLElement>) {
    this.items$ = this.entities$.pipe(
      switchMap((entities) => {
        if (!entities || entities.length === 0) return of([]);

        const observables = this.entities().map((entity, index) =>
          this.mediaOf()(entity).pipe(
            map((media) => (media ? { entity, media, index } : null)),
            catchError((err) => {
              this.logger.error(`Failed to load media for entity ${entity.id}`, err);
              return of(null); // skip this item
            })
          )
        );

        return combineLatest(observables).pipe(
          map((items) =>
            items.filter((item): item is MediaGridItem<T> => item !== null)
          )
        );
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('entities' in changes) {
      this.entities$.next(this.entities());
    }
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        this.setLayoutWidth(width);
      }
    });

    if (this.elementRef.nativeElement) {
      this.resizeObserver.observe(this.elementRef.nativeElement);
      this.setLayoutWidth(this.elementRef.nativeElement.clientWidth);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private setLayoutWidth(width: number) {
    this.columns = this.getColumns(width);
  }

  get columns(): number | undefined {
    return this._columns;
  }

  set columns(value: number | undefined) {
    if (value === undefined) return;

    value = Math.min(10, Math.max(1, value));
    if (value !== this._columns) {
      const oldClass = this._columns ? 'c' + this._columns : '';
      const newClass = 'c' + value;
      this._columns = value;

      const host = this.elementRef.nativeElement;
      if (oldClass) host.classList.remove(oldClass);
      host.classList.add(newClass);
    }
  }

  private getColumns(width: number): number {
    if (width >= 1600) return 5;
    if (width >= 1260) return 4;
    if (width >= 760) return 3;
    if (width >= 480) return 2;
    return 1;
  }

  onSelect(entity: T) {
    this.select.emit(entity);
  }
}
